/**
 * beyanOnDoldurma.js - what "Beyanı Düzenle" opens the Proje Beyanı wizard on (#677, 2026-11-03). Pure helpers, no React, no services (the two
 * catalogue calls of the type cascade are passed in) - tested in beyanOnDoldurma.test.js.
 *
 * Before this the wizard opened EMPTY on a declared stand and its /declare body carried `declaredKw: null`; the backend reads an absent kW as
 * "cleared", so declaring again silently wiped the kW the Teknik Talepler step had written (and the staff card then said "Talep yok" while the
 * Teknik talep still held it). Now the form starts from the declaration on the project, and every /declare keeps what the user did not touch.
 */

const text = (v) => (v == null ? "" : String(v));

/**
 * The wizard form fields a stored declaration (projeBeyani of GET /projects/by-stand) fills in. {} when nothing was declared yet, so a first
 * declaration still opens on the empty form. `wallIds` are the ids of the kroki's wall points: the stored walls are an ordered list without
 * positions, so they are laid on the points in that order (a re-saved list keeps its order, so nothing is lost).
 */
export function formFromDeclaration(pb, wallIds = []) {
  if (!pb || pb.declared !== true) return {};
  const wallWidths = {};
  const wallHeights = {};
  (pb.walls ?? []).slice(0, wallIds.length).forEach((w, i) => {
    wallWidths[wallIds[i]] = text(w?.width);
    wallHeights[wallIds[i]] = text(w?.height);
  });
  // The answers map is the truth; a declaration from before it existed only has the two legacy flags.
  const answers = pb.declaredKullanimlar && typeof pb.declaredKullanimlar === "object"
    ? { ...pb.declaredKullanimlar }
    : Object.fromEntries(
      [["CAM", pb.declaredGlass], ["BOYA", pb.declaredPaint]].filter(([, v]) => v != null),
    );
  return {
    m2: text(pb.declaredAreaM2),
    hMax: text(pb.declaredHeightNumber),
    kullanimlar: answers,
    wallWidths,
    wallHeights,
    // The Elektrik card of the Teknik step: the declared kW stays on while nothing says otherwise (see formFromTeknik).
    ...(pb.declaredKw != null ? { elektrikEnabled: true, elektrik: text(pb.declaredKw) } : {}),
  };
}

/**
 * The Teknik step fields the stand's Teknik requests (GET .../teknik) fill in. Saving that step REPLACES all four requests, so a form that
 * opened on the defaults would cancel the Su / Hava / İnternet the company already asked for. Elektrik: a request wins; with none, the kW of
 * the declaration (already in the form) stays; with neither the form keeps its default.
 */
export function formFromTeknik(rows) {
  if (!Array.isArray(rows)) return {};
  const by = Object.fromEntries(rows.map((r) => [r.categoryKey, r]));
  const out = {};
  if (by.elektrik?.hasRequest === true) {
    out.elektrikEnabled = true;
    out.elektrik = text(by.elektrik.quantity?.value);
  }
  ["su", "hava", "internet"].forEach((k) => { if (by[k]) out[k] = by[k].hasRequest === true; });
  return out;
}

/**
 * The kW every /declare (and the dry run) must carry: the one this wizard session already sent, else the one on the declaration when it was
 * opened, else none. null is only sent when there never was one.
 */
export function beyanKwKorunan(beyan, pbAtOpen) {
  return beyan?.declaredKw ?? pbAtOpen?.declaredKw ?? null;
}

/**
 * The permit fields of the SECOND /declare (the one that writes a changed kW from the Teknik step). A declaration stored with a permit still
 * breaks its rule, so a /declare without the flag is refused ("the kW could not be updated"); the flag goes again with the note that was given.
 * {} when the declaration holds no permit.
 */
export function ozelIzinTekrar(beyan, not = "") {
  if (!(beyan?.ozelIzinBekliyor === true || beyan?.ozelIzinli === true)) return {};
  const note = String(not ?? "").trim() || beyan?.ozelIzin?.talepNotu || null;
  return { ozelIzinTalep: true, ozelIzinNotu: note };
}

/**
 * The cascade levels that end on an already declared type, so the type step opens on it: one level per step down the project's root type, the
 * path's node selected in each, and a last empty level when the declared type itself has sub-types. null when the type is not under the root.
 * Same shape as the levels the wizard builds by hand: { parentId, options, selectedId }.
 */
export async function cascadeSeviyeleri(rootId, declaredTypeId, { getStandTypePath, getStandTypes }) {
  const path = (await getStandTypePath(declaredTypeId, { forceLive: true })) || [];
  const ids = path.map((p) => Number(p.standTypeId));
  const rootAt = ids.indexOf(Number(rootId));
  if (rootAt < 0) return null;
  const chain = ids.slice(rootAt);
  const levels = [];
  for (let i = 0; i < chain.length - 1; i += 1) {
    const options = (await getStandTypes(chain[i], { forceLive: true })) || [];
    levels.push({ parentId: chain[i], options, selectedId: String(chain[i + 1]) });
  }
  const last = chain[chain.length - 1];
  if (chain.length > 1) {
    const children = (await getStandTypes(last, { forceLive: true })) || [];
    if (children.length > 0) levels.push({ parentId: last, options: children, selectedId: "" });
  }
  return levels;
}
